<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

export interface ContextMenuItem {
    label: string;
    icon?: string;
    disabled?: boolean;
    onSelect: () => void;
}

const props = defineProps<{
    position: { x: number; y: number } | null;
    items: ContextMenuItem[];
}>();

const emit = defineEmits<{ close: [] }>();

const menuRef = ref<HTMLElement | null>(null);
const adjustedPosition = ref<{ x: number; y: number } | null>(null);
// Hide the menu for one frame so the un-clamped position never paints —
// otherwise wide menus opened near the right edge would flash off-screen
// before the measure-and-clamp pass.
const ready = ref(false);

const VIEWPORT_MARGIN = 8;

watch(
    () => props.position,
    async (pos) => {
        if (!pos) {
            adjustedPosition.value = null;
            ready.value = false;
            return;
        }
        adjustedPosition.value = pos;
        ready.value = false;
        await nextTick();
        const el = menuRef.value;
        if (!el) {
            ready.value = true;
            return;
        }
        const rect = el.getBoundingClientRect();
        let { x, y } = pos;
        if (x + rect.width + VIEWPORT_MARGIN > window.innerWidth) {
            x = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.width - VIEWPORT_MARGIN);
        } else if (x < VIEWPORT_MARGIN) {
            x = VIEWPORT_MARGIN;
        }
        if (y + rect.height + VIEWPORT_MARGIN > window.innerHeight) {
            y = Math.max(VIEWPORT_MARGIN, window.innerHeight - rect.height - VIEWPORT_MARGIN);
        } else if (y < VIEWPORT_MARGIN) {
            y = VIEWPORT_MARGIN;
        }
        adjustedPosition.value = { x, y };
        ready.value = true;
    },
    { immediate: true },
);

const onItemClick = (item: ContextMenuItem) => {
    if (item.disabled) return;
    item.onSelect();
    emit('close');
};

const onWindowClick = (event: MouseEvent) => {
    if (!props.position) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.app-context-menu')) return;
    emit('close');
};

const onWindowKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && props.position) emit('close');
};

onMounted(() => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onWindowKeydown);
});

onBeforeUnmount(() => {
    window.removeEventListener('click', onWindowClick);
    window.removeEventListener('keydown', onWindowKeydown);
});
</script>

<template>
    <div
        v-if="adjustedPosition"
        ref="menuRef"
        class="app-context-menu"
        :style="{
            top: adjustedPosition.y + 'px',
            left: adjustedPosition.x + 'px',
            visibility: ready ? 'visible' : 'hidden',
        }"
    >
        <button
            v-for="(item, idx) in items"
            :key="idx"
            type="button"
            class="app-context-menu-item"
            :class="{ 'app-context-menu-item-disabled': item.disabled }"
            :disabled="item.disabled"
            @click="onItemClick(item)"
        >
            <span v-if="item.icon" class="material-icons">{{ item.icon }}</span>
            <span>{{ item.label }}</span>
        </button>
    </div>
</template>

<style scoped>
.app-context-menu {
    position: fixed;
    z-index: var(--z-popover);
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    padding: 4px 0;
    min-width: 200px;
    display: flex;
    flex-direction: column;
}

.app-context-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    text-align: left;
}

.app-context-menu-item:not(.app-context-menu-item-disabled):hover {
    background: var(--color-row-hover);
}

.app-context-menu-item-disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.app-context-menu-item .material-icons {
    font-size: 18px;
    color: var(--color-text-secondary);
}
</style>
